import { motion } from "framer-motion";
import { Fish, Ship, UtensilsCrossed, Heart } from "lucide-react";
import { IMAGES } from "@/data";
import ChapterMark from "@/components/ChapterMark";

const HISTORY_POINTS = [
    {
        icon: Fish,
        title: "A fishery first",
        detail: "For tens of thousands of years the Parramatta River fed the Wallumedegal, Burramattagal and Wangal peoples. Its eel fishery was so central that Parramatta takes its name from Burramattagal, the place where the eels lie down.",
    },
    {
        icon: Ship,
        title: "Trading and travel routes",
        detail: "The river and its foreshores formed a network of fishing grounds, camps and trading routes, linking the clans of the harbour long before the colony arrived.",
    },
    {
        icon: UtensilsCrossed,
        title: "A river that can no longer feed anyone",
        detail: "Today, NSW Health advises against eating any seafood caught west of the Harbour Bridge, with elevated dioxins detected in the river's fish.",
    },
    {
        icon: Heart,
        title: "Former glory",
        detail: "We are locals. We grew up on this river, and we want to see it restored to what it was: swimmable, fishable and alive.",
    },
];

export default function RiverHistory() {
    return (
        <section id="history" className="relative overflow-hidden scroll-mt-24 py-24 md:py-32 bg-[#041222] border-y border-[#12283C]" data-testid="history-section">
            <div
                className="absolute inset-0 bg-cover bg-center opacity-[0.06] pointer-events-none"
                style={{ backgroundImage: `url(${IMAGES.wave})` }}
                aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#041222] via-transparent to-[#041222] pointer-events-none" aria-hidden="true" />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                    <ChapterMark index="02" label="The River's Story" testid="history-eyebrow" />
                    <h2 className="font-display font-semibold tracking-tight text-4xl md:text-5xl" data-testid="history-heading">
                        A river with a long memory
                    </h2>
                    <div className="mt-8 flex flex-col" data-testid="history-points">
                        {HISTORY_POINTS.map((point, i) => {
                            const Icon = point.icon;
                            return (
                                <motion.div
                                    key={point.title}
                                    initial={{ opacity: 0, y: 14 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.45, delay: i * 0.08 }}
                                    className="flex gap-4 py-5 border-b border-[#12283C] last:border-b-0"
                                    data-testid={`history-point-${i}`}
                                >
                                    <Icon className="w-5 h-5 mt-1 shrink-0 text-sky-200" />
                                    <div>
                                        <p className="text-lg font-semibold text-slate-100">{point.title}</p>
                                        <p className="mt-1 text-base text-slate-400 leading-relaxed">{point.detail}</p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0, x: 24 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6 }}
                    className="relative rounded-xl overflow-hidden border border-[#12283C]"
                    data-testid="history-image"
                >
                    <img
                        src="/images/parramatta-aerial.jpg"
                        alt="Aerial view of the Parramatta River with a ferry wake, looking toward the Sydney skyline"
                        className="w-full h-[420px] lg:h-[560px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#020D18]/85 via-transparent to-transparent" />
                    <div className="absolute bottom-0 p-6">
                        <p className="text-sm font-semibold tracking-[0.25em] uppercase text-sky-200">The river today</p>
                        <p className="mt-2 text-base text-slate-300 leading-relaxed">
                            The Parramatta River looking toward the city, photographed from above. Still the
                            artery of Western Sydney.
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
